import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDirective } from '@jsverse/transloco';
import { timer } from 'rxjs';

import { AuthService } from '../../../auth/auth.service';
import { ErrorPage } from '../error-page';
import { Button } from '../../../../shared/components/button/button';

/** Seconds until the page retries the boot on its own, like a display would. */
const RETRY_SECONDS = 30;

/**
 * Dead-end page for a boot that never completed: the splash watchdog lands
 * here when no route resolves within its timeout (see AppComponent), which
 * means user/org context could not be established.
 *
 * Retry (manual or the countdown) does a full page load of "/" rather than
 * reloading the current URL: a reload of /load-failed would just re-render
 * this page even after the backend recovered, while "/" re-runs the whole
 * boot chain and only lands back here if context is still dead.
 */
@Component({
  selector: 'app-load-failed',
  imports: [TranslocoDirective, ErrorPage, Button],
  template: `
    <ng-container *transloco="let t; read: 'errors.loadFailed'">
      <app-error-page
        code="503"
        [title]="t('title')"
        [body]="t('body')"
        [rows]="[
          { label: t('rows.workspace'), value: t('rows.failed'), badge: true },
          {
            label: t('rows.nextAttempt'),
            value: t('rows.inSeconds', { seconds: retryIn() }),
          },
        ]"
      >
        <app-button variant="secondary" [label]="t('signOut')" (activated)="signOut()" />
        <app-button iconLeft="icon-update" [label]="t('tryAgain')" (activated)="retry()" />
      </app-error-page>
    </ng-container>
  `,
})
export class LoadFailedPage {
  private readonly authService = inject(AuthService);

  protected readonly retryIn = signal(RETRY_SECONDS);

  constructor() {
    timer(1_000, 1_000)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        const remaining = this.retryIn() - 1;
        this.retryIn.set(Math.max(0, remaining));
        // Strict equality: fires exactly once even though the browser keeps
        // ticking while the navigation from retry() is in flight.
        if (remaining === 0) this.retry();
      });
  }

  retry(): void {
    location.assign('/');
  }

  signOut(): void {
    this.authService.logout();
  }
}
